import { FolderUpIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { DropEvent } from "react-aria";
import { isFileDropItem } from "react-aria-components";

import type { UploadDocument, UploadResults } from "@blis/shared/models/upload";
import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DropZone } from "@/components/ui/drop-zone";
import { Description, Input, Label, TextField } from "@/components/ui/field";
import { FileTrigger } from "@/components/ui/file-trigger";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Modal } from "@/components/ui/modal";
import { Radio, RadioField, RadioGroup } from "@/components/ui/radio-group";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { formatFileSize } from "@/lib/utils/units";
import { useDialogProps, useSetDialogState } from "../detail/state";

type UploadDialogProps = {
  accession: { id: number; subjectId: number };
};

export function UploadDialog(props: UploadDialogProps) {
  const dialogProps = useDialogProps("upload");
  const setResultsDialog = useSetDialogState("results");

  const initialState = dialogProps.open;

  const [filename, setFilename] = useState<string>("");
  const [droppedFiles, setDroppedFiles] = useState<File[] | undefined>(
    undefined,
  );

  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState("document");
  const [sendReportNotification, setSendReportNotification] = useState(false);

  const utils = trpc.useUtils();

  const onDropHandler = async (e: DropEvent) => {
    const items = e.items.filter(isFileDropItem);

    const first = items[0];
    if (!first) return;

    const file = await first.getFile();
    setDroppedFiles([file]);
    setFilename(file.name);
  };

  const onSubmit = async () => {
    if (!droppedFiles || droppedFiles.length === 0) return;

    setUploading(true);

    const formData = new FormData();
    formData.append("subjectId", props.accession.subjectId.toString());
    formData.append("accessionId", props.accession.id.toString());
    formData.append("type", selectedType);
    formData.append(
      "sendReportNotification",
      sendReportNotification.toString(),
    );
    const name = filename.trim();
    if (name) {
      formData.append("name", name);
    }

    for (const file of droppedFiles) {
      formData.append("files", file);
    }

    try {
      const res = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const data = (await res.json()) as { message: string };
        throw new Error(data?.message ?? "Failed to upload files");
      }
      const data = (await res.json()) as UploadResults | UploadDocument;

      // close dialog
      dialogProps.onOpenChange?.(null);
      setDroppedFiles(undefined);

      // invalidate / refetch uploads list
      utils.accession.uploads.list.invalidate();
      utils.accession.counts.invalidate();

      // open results dialog if appropriate
      if (data.type === "results") {
        setResultsDialog(data.result);
      } else {
        toast.success("Files uploaded");
      }
    } catch (err) {
      toast.error("Error uploading files", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setUploading(false);
    }
  };

  const onSelectHandler = async (e: FileList | null) => {
    if (e) {
      const files = Array.from([...e]);
      setDroppedFiles(files);
      const item = files[0];

      if (item) {
        setDroppedFiles([item]);
        setFilename(item.name);
      }
    }
  };

  useEffect(() => {
    if (!dialogProps.open) {
      setSelectedType("document");
      setDroppedFiles(undefined);
      setFilename("");
    }
  }, [dialogProps.open]);

  useEffect(() => {
    if (initialState?.type) {
      setSelectedType(initialState.type);
    }

    if (initialState?.files) {
      setDroppedFiles(initialState.files);
      setFilename(initialState.files[0]?.name ?? "");
      const isXml = ["text/xml", "application/xml"].includes(
        initialState.files[0]?.type ?? "",
      );
      setSelectedType(isXml ? "results" : initialState.type || "document");
    }
  }, [initialState]);

  return (
    <Modal
      isOpen={!!dialogProps.open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          dialogProps.onOpenChange(null);
        }
      }}
      size="xl"
      aria-label="Upload document or results"
    >
      <Dialog>
        <DialogHeader>
          <DialogTitle>Upload document or results</DialogTitle>
        </DialogHeader>
        <DialogBody className="gap-4">
          {droppedFiles && droppedFiles.length > 0 ? (
            <>
              <div className="flex flex-col">
                <Label>File to upload</Label>
                <GridList className="w-full" aria-label="Uploaded files">
                  {droppedFiles.map((file, idx) => (
                    <GridListItem
                      key={`${file.name}-${idx}`}
                      id={`${file.name}-${idx}`}
                      className="flex items-center justify-between gap-4"
                      textValue={file.name}
                    >
                      <div className="flex flex-col min-w-0">
                        <Text elementType="p" truncate>
                          {file.name}
                        </Text>
                        <Text muted>{formatFileSize(file.size)}</Text>
                      </div>

                      <Button
                        variant="outline"
                        size="icon"
                        className="size-6"
                        onPress={() => {
                          setDroppedFiles((files) =>
                            files?.filter((_f, i) => idx !== i),
                          );
                        }}
                      >
                        <XIcon className="size-3" />
                      </Button>
                    </GridListItem>
                  ))}
                </GridList>
              </div>
              <TextField
                value={filename}
                onChange={(value) => setFilename(value)}
              >
                <Label>Name</Label>
                <Input />
              </TextField>
            </>
          ) : (
            <DropZone
              onDrop={onDropHandler}

              // getDropOperation={(types) =>
              //   types.has("image/jpeg") || types.has("image/png")
              //     ? "copy"
              //     : "cancel"
              // }
              // getDropOperation={(types, allowedOperations) => {
              //   console.log({ types, allowedOperations });
              //   return "copy";
              // }}
            >
              <div className="grid space-y-3">
                <div className="size-12 mx-auto grid place-content-center rounded-full border bg-secondary/70 group-data-[drop-target]:bg-primary/20 group-data-[drop-target]:border-primary/70">
                  <FolderUpIcon data-slot="icon" className="size-5" />
                </div>
                <div className="justify-center flex">
                  <FileTrigger
                    // acceptedFileTypes={["text/xml", "application/xml", "application/pdf"]}
                    allowsMultiple={false}
                    onSelect={onSelectHandler}
                  >
                    Upload file
                  </FileTrigger>
                </div>
                <Description>Or drag and drop a file here</Description>
              </div>
            </DropZone>
          )}
          <RadioGroup
            aria-label="Select file type"
            value={selectedType}
            onChange={(value) => {
              setSelectedType(value);
            }}
          >
            <RadioField>
              <Radio value="document">Document</Radio>
              <Description>
                A generic file upload to be attached to this accession.
              </Description>
            </RadioField>
            <RadioField>
              <Radio value="report">Report</Radio>
              <Description>
                The file contains test results that should be displayed as a
                report.
              </Description>
            </RadioField>
            <RadioField>
              <Radio value="results">Test results</Radio>
              <Description>
                The file contains test results that should be parsed and entered
                (HLA only).
              </Description>
            </RadioField>
          </RadioGroup>
          {selectedType === "report" && (
            <Checkbox
              isSelected={sendReportNotification}
              onChange={setSendReportNotification}
            >
              Send report notification
            </Checkbox>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            isPending={uploading}
            isDisabled={uploading}
            onPress={onSubmit}
          >
            Upload
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
